<template>
  <div id="ls-leads-conversation-screen">
    <div v-if="contact && contact.id" class="ls-leads-conversation-screen--main">
      <div class="ls-leads-conversation-screen--header">
        <div class="ls-leads-conversation-screen--header-inner">
          <div class="ls-leads-conversation-screen--header-inner--left">
            <div
              v-if="!screenSize.desktop"
              class="ls-leads-conversation-screen--back-wrapper"
              test-id="back"
              @click="goBack"
            >
              <div class="ls-leads-conversation-screen--chevron">
                <LsIcon type="chevron" size="md" />
              </div>
              <span class="ls-leads-conversation-screen--back">INBOX</span>
            </div>
          </div>
          <div class="ls-leads-conversation-screen--header-inner--right">
            <div v-if="!screenSize.desktop">
              <h1
                class="ls-leads-conversation-screen--name has-text-weight-bold"
                test-id="contractor_name"
              >
                {{ contact.name }}
              </h1>
              <div
                class="ls-leads-view-profile"
                @click="showProviderCardOnMobile = !showProviderCardOnMobile"
              >
                <div class="ls-leads-view-profile--text">View Profile</div>
                <LsIcon type="chevron" size="md" />
              </div>
            </div>
          </div>
          <div class="ls-leads-conversation-screen--header--bottom">
            <div :key="contact.id" class="ls-services--leads-card-wrapper">
              <div class="columns">
                <div
                  v-if="screenSize.desktop || showProviderCardOnMobile"
                  :class="[
                    screenSize.desktop
                      ? 'column is-three-quarters'
                      : 'column is-full ls-services--leads-card--mobile-avatar',
                  ]"
                >
                  <LsProviderInfoCardMore
                    :id="contact.id"
                    :contractor="contractor"
                    :property="property"
                  />
                </div>
                <div :class="[screenSize.desktop ? 'column is-one-quarter' : 'column is-full']">
                  <LsButton
                    v-if="contact.status !== 'hired' && contact.status !== 'closed'"
                    class="ls-leads-conversation-screen--hire-pro"
                    text="hire pro"
                    track-id="services_view-hire_pro"
                    @click="onOpenConfirmHiredModal"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        :class="['ls-leads-conversation-screen--messages-wrapper', { columns: screenSize.desktop }]"
      >
        <div :class="{ 'column is-12': screenSize.desktop }">
          <div
            id="ls-leads-conversation-screen--messages"
            class="ls-leads-conversation-screen--messages"
          >
            <div v-if="messages.length">
              <LeadsConversationMessage
                v-for="(message, idx) in messages"
                :key="message.id"
                :message="message"
                :is-last="messageBeingSaved ? false : idx === messages.length - 1"
              />
              <LeadsConversationMessage
                v-if="messageBeingSaved"
                id="ls-temp-message"
                :message="messageBeingSaved"
                is-last
              />
            </div>
            <div v-else class="ls-leads-conversation-screen--placholder">
              <p
                v-if="!contact.is_messaging_active && contact.status !== 'closed'"
                class="ls-leads-conversation-screen--placholder-text"
              >
                This is the beginning of your conversation with {{ contact.name }}.
              </p>
            </div>
          </div>
          <form v-if="contact.status !== 'closed'" novalidate @submit.prevent="sendMessage">
            <span class="ls-leads-conversation-screen--new-message-error">
              {{ errors.first('ls-leads-conversation-screen--new-message-images') }}
            </span>
            <div class="ls-leads-conversation-screen--input-wrapper columns">
              <div class="column is-10">
                <div class="ls-leads-conversation-screen--new-message-form">
                  <BField
                    :type="{ 'is-error': getValidation.invalid }"
                    :message="getValidation.message"
                  >
                    <BUpload
                      v-model="localImages"
                      v-validate="'maxImages'"
                      multiple
                      test-id="ls_upload_photos"
                      name="ls-leads-conversation-screen--new-message-images"
                    >
                      <div class="ls-leads-conversation-screen--icon-wrapper">
                        <div><LsIcon type="photo" /></div>
                      </div>
                    </BUpload>
                  </BField>
                  <div class="ls-leads-conversation-screen--new-message-input-wrapper">
                    <LsInput
                      v-model="localMessage"
                      track-id="ls_leads_conversation_screen-message"
                      name="local-message"
                      placeholder="Write a message..."
                      type="textarea"
                    />

                    <BCollapse class="panel" :open.sync="hasImages">
                      <div v-if="localImages.length" class="pending-imgs">
                        <div v-for="(file, i) in localImages" :key="i">
                          <img :src="getImage(file)" />
                          <LsButton
                            track-id="ls_leads_conversation_screen-image_delete"
                            test-id="delete"
                            icon="close"
                            bg-color="primary"
                            class="delete-btn"
                            @click="deleteDropFile(idx)"
                          />
                        </div>
                      </div>
                    </BCollapse>
                  </div>
                </div>
              </div>
              <div class="ls-leads-conversation-screen--button-wrapper">
                <LsButton
                  test-id="send"
                  track-id="ls_conversation_screen-send_message"
                  bg-color="primary"
                  text="send"
                  :min-width="false"
                  :disabled="!localMessage.length && !localImages.length"
                  type="submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div v-if="!contact" class="ls-leads-conversation-screen--placholder">
      <p class="ls-leads-conversation-screen--placholder-text">There are no current messages.</p>
    </div>
    <BLoading
      :active.sync="customerLeads.loadingStatus.isLoading || customerLeads.loadingStatus.isUpdating"
      :is-full-page="false"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import _ from 'lodash';
import VueTypes from 'vue-types';
import LeadsConversationMessage from '@/views/Authorized/Leads/LeadsInboxView/LeadsConversation/LeadsConversationMessage.vue';
import LsProviderInfoCardMore from '@/components/LsProviderInfoCardMore/LsProviderInfoCardMore.vue';
import { LsInput, LsButton, LsIcon } from '@/components/common';
import { lsPropTypeHelpers, lsValidationService } from 'ls-vue-common';
import { CUSTOMER_LEADS__SEND_MESSAGE } from '@/store/types';
import VueScrollTo from 'vue-scrollto';

const MAX_IMAGES = 25;

export default {
  name: 'LeadsConversationMessages',
  inject: ['$validator'],
  components: {
    LeadsConversationMessage,
    LsProviderInfoCardMore,
    LsInput,
    LsButton,
    LsIcon,
  },
  props: {
    contractor: VueTypes.shape({
      user: VueTypes.shape({
        first_name: VueTypes.string,
        last_name: VueTypes.string,
        profile_image_url: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      }).isRequired.loose,
      metrics: VueTypes.shape({
        ratings: VueTypes.shape({
          average: VueTypes.number.isRequired,
        }).isRequired.loose,
      }).isRequired.loose,
      name: VueTypes.string,
      city: VueTypes.string,
      state: VueTypes.string,
      years_of_service: VueTypes.number,
    }).isRequired.loose,
    contact: VueTypes.shape({
      id: VueTypes.number,
      name: VueTypes.string,
      is_messaging_active: VueTypes.bool,
      status: VueTypes.string,
    }).isRequired.loose,
    messages: VueTypes.arrayOf({
      id: VueTypes.number,
      text: VueTypes.string,
      images: VueTypes.arrayOf(VueTypes.object),
    }),
  },
  data() {
    return {
      localImages: [],
      localMessage: '',
      messageBeingSaved: null,
      showProviderCardOnMobile: false,
    };
  },
  computed: {
    ...mapState(['customerLeads', 'properties', 'screenSize']),
    property() {
      return this.properties.propertiesById[this.$route.params.property_id];
    },
    hasImages() {
      return _.size(this.localImages) > 0;
    },
  },
  watch: {
    customerLeads: {
      handler() {
        if (!this.customerLeads.loadingStatus.isLoading) {
          this.scrollToBottom();
        }
      },
      // if watching anything beyond first level of object, need deep
      deep: true,
    },
  },
  created() {
    if (!this.customerLeads.loadingStatus.isLoading) {
      this.scrollToBottom();
    }
    this.$validator.extend('maxImages', {
      getMessage: () => `You cannot upload more than ${MAX_IMAGES} images`,
      validate: (value) => {
        return new Promise((resolve) => {
          resolve({
            valid: value.length <= MAX_IMAGES,
          });
        });
      },
    });
  },
  methods: {
    ...mapActions([CUSTOMER_LEADS__SEND_MESSAGE]),
    onOpenConfirmHiredModal() {
      this.$emit('onOpenConfirmHiredModal');
    },
    async sendMessage() {
      const valid = await this.$validator.validateAll();

      if (!valid) {
        return;
      }
      this.$emit('onSendMessage', {
        message: this.localMessage,
        images: this.localImages,
      });

      this.localMessage = '';
      this.localImages = [];
    },
    scrollToBottom() {
      if (this.messages.length) {
        // setTimeout needed to allow full height of div or body to load prior to scrolling

        setTimeout(() => {
          const messageDiv = this.screenSize.desktop
            ? document.getElementById('ls-leads-conversation-screen--messages')
            : document.body;

          const html = document.documentElement;
          // calculate the full height of the body if using body, but also works for the div.
          const height = Math.max(
            messageDiv.scrollHeight,
            messageDiv.offsetHeight,
            html.clientHeight,
            html.scrollHeight,
            html.offsetHeight
          );

          if (messageDiv) {
            VueScrollTo.scrollTo(messageDiv, {
              easing: 'ease-in-out',
              container: messageDiv,
              duration: 300,
              offset: height,
            });
          }
        }, 100);
      }
    },
    getValidation() {
      return lsValidationService.getValidation.call(this);
    },
    goBack() {
      this.$emit('onBackToInbox');
    },
    deleteDropFile(idx) {
      this.localImages.splice(idx, 1);
      this.$emit('input', this.localImages);
    },
    getImage(file) {
      return URL.createObjectURL(file);
    },
  },
};
</script>
<style lang="scss">
#ls-leads-conversation-screen {
  .textarea:not([rows]) {
    height: auto;
    min-height: 45px;
    border: none;
    resize: none;
  }
}
</style>
<style lang="scss" scoped>
#ls-leads-conversation-screen {
  display: flex;
  align-items: center;
  height: 60vh;
  position: relative;
}

.ls-leads-conversation-screen--main,
.ls-leads-conversation-screen--messages-wrapper {
  height: 100%;
  width: 100%;
}

.ls-leads-conversation-screen--header {
  width: 100%;
  display: flex;
  align-items: baseline;
  padding: 10px 0 20px 0;
  border-bottom: 2px solid $primary;
  margin-bottom: 20px;
}

.ls-leads-view-profile {
  display: flex;
  width: 100%;
  align-items: center;
}

.ls-leads-view-profile--text {
  color: $primary;
  font-weight: bold;
  text-align: right;
}

.ls-leads-conversation-screen--business-name {
  font-size: $size-5;
  padding-left: 10px;
  color: $primary;
}

.ls-leads-conversation-screen--messages {
  overflow-y: scroll;
  overflow-x: hidden;
  height: 100%;
  width: 633px;
}

.ls-leads-conversation-screen--messages::-webkit-scrollbar {
  width: 20px;
  background-color: $white;
}

.ls-leads-conversation-screen--messages::-webkit-scrollbar-thumb {
  background-color: $gray-tertiary;
  border: 6px solid $white;
}

.ls-leads-conversation-screen--placholder {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.ls-leads-conversation-screen--placholder-text {
  text-align: center;
  font-size: $size-4;
  color: $primary;
}

.ls-leads-conversation-screen--archived {
  font-size: $size-6;
  color: $white;
  padding: 10px;
  background-color: $tertiary;
}

.ls-leads-conversation-screen--input-wrapper {
  .input {
    border: none;
  }
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  border-top: 2px solid $primary;
  overflow: auto;
  height: auto;
}

.ls-leads-conversation-screen--button-wrapper {
  padding-top: 10px;
  button {
    min-width: 96px;
  }
}

.ls-leads-conversation-screen--new-message-form {
  display: flex;
  align-items: flex-start;
  .field {
    margin-right: 10px;
  }
}

.pending-imgs {
  display: block;
  div {
    margin: 0 0 10px 5px;
    img {
      object-fit: cover;
      width: 170px;
      height: 110px;
      @media screen and (min-width: $desktop) {
        width: 170px;
        height: 110px;
      }
    }
  }
}

.delete-btn {
  margin-top: 2px;
  margin-left: -50px;
}

.ls-leads-conversation-screen--new-message-input-wrapper {
  flex: 1;
  border: 1px solid $primary;
  overflow: auto;
  padding: 10px;
  resize: vertical;
  .textarea {
    box-shadow: none;
    border: none;
    resize: none;
  }
}

.ls-leads-conversation-screen--icon-wrapper {
  display: flex;
  align-items: center;
  justify-items: center;
  border-radius: 50%;
  height: 35px;
  width: 35px;
  margin-right: 2px;
  background-color: $primary-light;
  &:hover {
    cursor: pointer;
  }
  div {
    display: flex;
    align-items: center;
    margin-left: auto;
    margin-right: auto;
  }
}

.ls-leads-conversation-screen--new-message-error {
  color: $danger;
  padding: 2px;
}

@media (max-width: $mobile-max-width) {
  #ls-leads-conversation-screen {
    display: flex;
    align-items: center;
  }

  .ls-leads-conversation-screen--header-inner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
  }

  .ls-leads-conversation-screen--header-inner--left {
    display: flex;
  }

  .ls-leads-conversation-screen--header-inner--right {
    display: flex;
    margin-left: auto;
  }

  .ls-leads-conversation-screen--header--bottom {
    width: 100%;
    border-bottom: none;
    button {
      width: 100%;
    }
  }

  .ls-leads-conversation-screen--header {
    z-index: $z-index-conversation-header;
    user-select: none;
    background-color: $white;
    position: fixed;
    top: $navbar-height;
    left: 0;
    padding: 20px 20px 20px 15px;
    justify-content: space-between;
    align-items: center;
  }

  .ls-leads-conversation-screen--back-wrapper {
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .ls-leads-conversation-screen--chevron {
    transform: rotate(180deg) translateY(2px);
  }

  .ls-leads-conversation-screen--back {
    color: $primary;
    font-weight: bold;
    position: relative;
  }
  .ls-leads-conversation-screen--name {
    color: $gray-primary;
    font-size: $size-4;
  }

  .ls-leads-conversation-screen--messages {
    padding: 205px 0 160px 0;
    width: 100%;
    height: 100%;
  }

  .ls-leads-conversation-screen--placholder {
    padding-top: 30px;
  }

  .ls-leads-conversation-screen--archived {
    position: fixed;
    bottom: 112px;
    left: 0;
    width: 100%;
  }

  .ls-leads-conversation-screen--input-wrapper {
    background: $white;
    display: block;
    position: fixed;
    bottom: 0px;
    left: 0px;
    width: 100%;
    margin: auto;
    padding: 10px;
  }

  .ls-leads-conversation-screen--hire-pro {
    margin-top: 8px;
  }
  .ls-services--leads-card-wrapper {
    margin-top: 5px;
    width: 100%;
  }
}
.themed-app {
  .ls-leads-conversation-screen--header {
    border-color: $theme-color-primary;
  }
  .ls-leads-view-profile--text {
    color: $theme-color-primary;
  }

  .ls-leads-conversation-screen--business-name {
    font-size: $theme-txt-body;
    color: $theme-color-primary;
  }
  .ls-leads-conversation-screen--messages::-webkit-scrollbar {
    background-color: $theme-color-white;
  }
  .ls-leads-conversation-screen--messages::-webkit-scrollbar-thumb {
    background-color: $gray-tertiary;
    border-color: $theme-color-white;
  }
  .ls-leads-conversation-screen--placholder-text {
    font-size: $theme-txt-body-lg;
    color: $theme-color-primary;
  }

  .ls-leads-conversation-screen--archived {
    font-size: $theme-txt-body-sm;
    color: $theme-color-white;
    background-color: $tertiary;
  }
  .ls-leads-conversation-screen--input-wrapper {
    border-color: $theme-color-primary;
  }
  .ls-leads-conversation-screen--new-message-input-wrapper {
    border-color: $theme-color-primary;
  }

  .ls-leads-conversation-screen--icon-wrapper {
    background-color: $theme-color-primary-light;
  }

  .ls-leads-conversation-screen--new-message-error {
    color: $theme-color-danger;
  }

  @media (max-width: $mobile-max-width) {
    .ls-leads-conversation-screen--header {
      background-color: $theme-color-white;
    }
    .ls-leads-conversation-screen--back {
      color: $theme-color-primary;
    }
    .ls-leads-conversation-screen--name {
      color: $gray-primary;
      font-size: $theme-txt-body-lg;
    }
    .ls-leads-conversation-screen--input-wrapper {
      background: $theme-color-white;
    }
  }
}
</style>
